import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { productsApi, profileApi } from '@/services/api';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { buildProductJsonLd, plainTextFromHtml, truncateMeta } from '@/utils/seo';
import { trackProductView } from '@/utils/behaviorTracking';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { ProductCard } from '@/features/products/ProductCard';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { usePricing } from '@/hooks/usePricing';
import { toast } from 'sonner';
import { 
  ShoppingCart, 
  Truck, 
  Store, 
  CheckCircle, 
  AlertCircle,
  Minus,
  Plus,
  ChevronLeft,
  ChevronRight,
  Tag,
  Heart
} from 'lucide-react';

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { displayPrice, vatLabel, isBusiness, priceExclVat, vatAmount: calcVat } = usePricing();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await productsApi.getOne(id);
        setProduct(response.data);
        trackProductView(response.data.id);
        
        // Track ViewContent för Meta Pixel
        if (window.trackViewContent) {
          window.trackViewContent({
            id: response.data.id,
            title: response.data.title,
            category: response.data.category_id,
            price: response.data.price
          });
        }
        
        // Fetch related products
        if (response.data.category_id) {
          const relatedResponse = await productsApi.getAll({ category: response.data.category_id, limit: 4 });
          setRelatedProducts(relatedResponse.data.products.filter(p => p.id !== id));
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Kunde inte hämta produkten');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (isAuthenticated && id) {
      profileApi.checkFavorite(id)
        .then(res => setIsFavorite(res.data.is_favorite))
        .catch(() => {});
    }
  }, [isAuthenticated, id]);

  const toggleFavorite = async () => {
    if (!isAuthenticated) { toast.info('Logga in för att spara favoriter'); return; }
    if (favLoading) return;
    setFavLoading(true);
    try {
      if (isFavorite) {
        await profileApi.removeFavorite(id);
        setIsFavorite(false);
        toast.success('Favorit borttagen');
      } else {
        await profileApi.addFavorite(id);
        setIsFavorite(true);
        toast.success('Tillagd som favorit');
      }
    } catch { toast.error('Kunde inte uppdatera favorit'); }
    finally { setFavLoading(false); }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Du måste vara inloggad för att handla');
      navigate('/logga-in');
      return;
    }

    setAdding(true);
    try {
      await addToCart(product.id, quantity, product);
      toast.success(`${product.title} har lagts i varukorgen`);
    } catch (error) {
      toast.error('Kunde inte lägga till i varukorgen');
    } finally {
      setAdding(false);
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? (product.images?.length || 1) - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === (product.images?.length || 1) - 1 ? 0 : prev + 1
    );
  };

  if (loading) {
    return (
      <div className="container-main py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="aspect-square bg-gray-100 animate-pulse rounded-md" />
          <div className="space-y-4">
            <div className="h-8 bg-gray-100 animate-pulse rounded w-3/4" />
            <div className="h-12 bg-gray-100 animate-pulse rounded w-1/2" />
            <div className="h-24 bg-gray-100 animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-main py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Produkten hittades inte</h1>
        <Link to="/produkter">
          <Button>Tillbaka till produkter</Button>
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    { label: 'Produkter', href: '/produkter' },
    ...(product.category_name ? [{ label: product.category_name, href: `/produkter?category=${product.category_id}` }] : []),
    { label: product.title }
  ];

  const images = product.images?.length > 0 
    ? product.images 
    : ['https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800'];

  const metaDescription =
    truncateMeta(plainTextFromHtml(product.description)) || truncateMeta(product.title);

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="product-detail-page">
      <SeoMeta
        title={product.title}
        description={metaDescription}
        canonicalPath={`/produkt/${product.id}`}
        ogImage={images[0]}
        ogType="product"
        jsonLd={buildProductJsonLd(product)}
      />
      <div className="container-main py-8">
        <Breadcrumbs items={breadcrumbs} />

        <div className="bg-white rounded-md shadow-sm overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Image Gallery */}
            <div className="relative bg-gray-100">
              <div className="aspect-square">
                <img
                  src={images[currentImageIndex]}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
              </div>
              {images.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md"
                    data-testid="prev-image"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md"
                    data-testid="next-image"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full ${index === currentImageIndex ? 'bg-[#003B73]' : 'bg-gray-300'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Product Info */}
            <div className="p-6 md:p-8 flex flex-col">
              <div className="flex-1">
                {/* Kampanjbadge */}
                {product.has_campaign && product.discount_percent > 0 && (
                  <div className="mb-4">
                    <span className="inline-flex items-center gap-1 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                      <Tag className="h-4 w-4" />
                      SPARA {product.discount_percent}%
                    </span>
                  </div>
                )}
                
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h1 className="text-2xl md:text-3xl font-black text-[#0F172A]" data-testid="product-title">
                    {product.title}
                  </h1>
                  <button
                    onClick={toggleFavorite}
                    disabled={favLoading}
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
                      isFavorite
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                        : 'bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500'
                    }`}
                    aria-label={isFavorite ? 'Ta bort favorit' : 'Lägg till favorit'}
                    data-testid="product-detail-favorite-btn"
                  >
                    <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
                  </button>
                </div>

                {/* Prissektion med kampanjpris */}
                <div className="mb-4" data-testid="product-price">
                  {product.has_campaign && product.original_price ? (
                    <div className="space-y-1">
                      <div className="flex items-baseline gap-3">
                        <span className="text-3xl md:text-4xl font-black text-red-600">
                          {displayPrice(product.price).toLocaleString('sv-SE')} kr
                        </span>
                        <span className="text-xl text-gray-400 line-through">
                          {displayPrice(product.original_price).toLocaleString('sv-SE')} kr
                        </span>
                      </div>
                      <p className="text-lg font-semibold text-green-600">
                        Du sparar {displayPrice(product.discount_amount).toLocaleString('sv-SE')} kr
                      </p>
                      <p className="text-sm text-gray-400">{vatLabel}</p>
                    </div>
                  ) : (
                    <div>
                      <div className="text-3xl md:text-4xl font-black text-[#003B73]">
                        {displayPrice(product.price).toLocaleString('sv-SE')} kr
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{vatLabel}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-6 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Truck className="h-5 w-5" />
                    <span>
                      Frakt: {product.shipping_cost > 0 ? `${product.shipping_cost} kr` : 'Gratis'}
                    </span>
                  </div>
                  <div className={`flex items-center gap-2 ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {product.stock > 0 ? (
                      <>
                        <CheckCircle className="h-5 w-5" />
                        <span>I lager ({product.stock} st)</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-5 w-5" />
                        <span>Slutsåld</span>
                      </>
                    )}
                  </div>
                </div>

                {product.store_name && (
                  <Link 
                    to={`/butik/${product.store_id}`}
                    className="flex items-center gap-2 text-[#003B73] hover:underline mb-6"
                    data-testid="store-link"
                  >
                    <Store className="h-5 w-5" />
                    <span className="font-semibold">{product.store_name}</span>
                  </Link>
                )}

                <div className="border-t border-gray-100 pt-6 mb-6">
                  <h3 className="font-bold text-[#0F172A] mb-3">Beskrivning</h3>
                  <div className="mb-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      product.condition === 'demo'
                        ? 'bg-amber-100 text-amber-700'
                        : product.condition === 'defect'
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {product.condition === 'demo' ? 'Demoex' : product.condition === 'defect' ? 'Defekt' : 'Ny vara'}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed" data-testid="product-description">
                    {product.description}
                  </p>
                </div>

                {product.specifications && Object.keys(product.specifications).length > 0 && (
                  <div className="border-t border-gray-100 pt-6 mb-6">
                    <h3 className="font-bold text-[#0F172A] mb-3">Specifikationer</h3>
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <React.Fragment key={key}>
                          <dt className="text-gray-500 capitalize">{key}</dt>
                          <dd className="font-medium text-[#0F172A]">{value}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                )}
              </div>

              {product.documents && product.documents.length > 0 && (
                <div className="border-t border-gray-100 pt-6 mb-6">
                  <h3 className="font-bold text-[#0F172A] mb-3">Dokument</h3>
                  <div className="flex flex-col gap-2">
                    {product.documents.map((doc, i) => (
                      <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#003B73] hover:text-[#FF8A00] transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        {doc.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {/* Add to Cart */}
              <div className="border-t border-gray-100 pt-6">
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm font-semibold text-gray-700">Antal:</span>
                  <div className="flex items-center border rounded-sm">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-2 hover:bg-gray-100"
                      data-testid="decrease-qty"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="px-4 py-2 font-semibold" data-testid="quantity">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="p-2 hover:bg-gray-100"
                      data-testid="increase-qty"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <Button
                  onClick={handleAddToCart}
                  disabled={adding || product.stock <= 0}
                  className="w-full h-14 bg-[#FF8A00] hover:bg-[#e67c00] text-white font-bold text-lg rounded-sm"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  {adding ? 'Lägger till...' : 'Lägg i varukorg'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl md:text-2xl font-black text-[#0F172A] mb-6">
              Relaterade produkter
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Sticky Add to Cart */}
      <div className="sticky-bottom md:hidden">
        <div className="flex items-center justify-between gap-4">
          <div>
            {product.has_campaign && product.original_price ? (
              <div>
                <div className="flex items-baseline gap-2">
                  <p className="font-black text-xl text-red-600">
                    {product.price.toLocaleString('sv-SE')} kr
                  </p>
                  <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded font-bold">
                    -{product.discount_percent}%
                  </span>
                </div>
                <p className="text-xs text-gray-400 line-through">
                  {product.original_price.toLocaleString('sv-SE')} kr
                </p>
                <p className="text-xs text-green-600 font-semibold">
                  Spara {product.discount_amount?.toLocaleString('sv-SE')} kr
                </p>
              </div>
            ) : (
              <div>
                <p className="font-black text-xl text-[#003B73]">
                  {product.price.toLocaleString('sv-SE')} kr
                </p>
                <p className="text-xs text-gray-500">
                  + {product.shipping_cost > 0 ? `${product.shipping_cost} kr frakt` : 'Fri frakt'}
                </p>
              </div>
            )}
          </div>
          <Button
            onClick={handleAddToCart}
            disabled={adding || product.stock <= 0}
            className="flex-1 h-12 bg-[#FF8A00] hover:bg-[#e67c00] text-white font-bold rounded-sm"
            data-testid="add-to-cart-mobile"
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            Köp
          </Button>
        </div>
      </div>
    </div>
  );
};
